import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, ErrorText, Flex, FormField, Heading, TextField } from '@cmaas/ui';
import { CmaasApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';

// admin_users.email is globally unique (docs/P5-NOTES.md D-P5a-9's
// tenant_id requirement is portal-only) — no tenant_id field here.
const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1, 'Password is required'),
});
type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  if (status === 'authenticated') {
    const from = (location.state as { from?: Location } | null)?.from;
    return <Navigate to={from?.pathname ?? '/'} replace />;
  }

  const onSubmit = async (values: FormValues) => {
    setServerError(null);
    try {
      await login(values.email, values.password);
      navigate('/', { replace: true });
    } catch (err) {
      setServerError(err instanceof CmaasApiError ? err.message : 'Login failed. Please try again.');
    }
  };

  return (
    <Box maxWidth="400px" mx="auto" mt="9">
      <Heading size="7" mb="5">
        CMaaS admin portal
      </Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap="3">
          <FormField label="Email" error={errors.email?.message}>
            <TextField.Root type="email" autoComplete="username" {...register('email')} />
          </FormField>
          <FormField label="Password" error={errors.password?.message}>
            <TextField.Root type="password" autoComplete="current-password" {...register('password')} />
          </FormField>
          {serverError && <ErrorText>{serverError}</ErrorText>}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </Button>
        </Flex>
      </form>
    </Box>
  );
}
